import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, Zap, Activity } from 'lucide-react';
import { Logo } from '@/components/Logo';

export default function Login() {
  const nav = useNavigate();
  const { setSession } = useAuthStore();
  const [form, setForm] = React.useState({ email: '', password: '' });
  const [loading, setLoading] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', form);
      setSession({ token: data.access_token, user: data.user });
      toast.success('Welcome back');
      nav('/dashboard');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Login failed');
    } finally { setLoading(false); }
  };

  return (
    <AuthLayout>
      <div className="w-full max-w-md">
        <Logo />
        <h1 className="mt-8 text-2xl font-semibold tracking-tight" data-testid="login-title">Sign in to Redserp</h1>
        <p className="mt-1 text-sm text-[hsl(var(--text-3))]">Operate long-running autonomous agents with precision.</p>
        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email" type="email" required autoFocus autoComplete="email"
              placeholder="you@company.com"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="login-email-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password" type="password" required autoComplete="current-password"
              placeholder="••••••••••••"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="login-password-input"
            />
          </div>
          <Button type="submit" disabled={loading} className="w-full" data-testid="login-submit-button">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Sign in
          </Button>
          <p className="text-sm text-[hsl(var(--text-3))]">
            Don't have an account? <Link to="/signup" className="text-[hsl(var(--primary))] hover:underline" data-testid="login-signup-link">Create one</Link>
          </p>
        </form>
      </div>
    </AuthLayout>
  );
}

export function AuthLayout({ children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[hsl(var(--background))]">
      <div className="relative flex items-center justify-center p-8">
        <div className="noise-overlay" />
        {children}
      </div>
      <div className="relative hidden lg:flex items-center justify-center overflow-hidden border-l border-white/5 bg-[hsl(var(--surface-1))]">
        <div className="noise-overlay" />
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="relative z-10 max-w-md px-8">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--primary))]">Operator console</div>
          <h2 className="mt-2 text-3xl font-semibold leading-tight tracking-tight">A precision instrument for autonomous social agents.</h2>
          <p className="mt-3 text-sm text-[hsl(var(--text-3))]">Orchestrate long-running Reddit karma, warmup and moderation agents on your own stealth infrastructure.</p>
          <ul className="mt-8 space-y-3 text-sm">
            <Feature icon={ShieldCheck} title="Stealth by default" desc="Camoufox + residential proxies + rotating fingerprints." />
            <Feature icon={Zap} title="Durable by design" desc="Temporal workflows survive restarts, crashes and deploys." />
            <Feature icon={Activity} title="Live observability" desc="Stream steps, screenshots, and risk signals in real time." />
          </ul>
          <div className="mt-10 flex items-center gap-3 text-[11px] uppercase tracking-wider text-[hsl(var(--text-3))]">
            <span className="rounded border border-white/10 px-2 py-1">Temporal</span>
            <span className="rounded border border-white/10 px-2 py-1">Postgres</span>
            <span className="rounded border border-white/10 px-2 py-1">R2</span>
            <span className="rounded border border-white/10 px-2 py-1">Upstash</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Feature({ icon: Icon, title, desc }) {
  return (
    <li className="flex items-start gap-3">
      <div className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-md border border-white/10 bg-[hsl(var(--surface-2))] text-[hsl(var(--primary))]">
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div>
        <div className="text-sm font-medium">{title}</div>
        <div className="text-xs text-[hsl(var(--text-3))]">{desc}</div>
      </div>
    </li>
  );
}
